// WebGL2 コンポジタ。Document を文書サイズの FBO に合成する。
// 表示 (viewRenderer) と書き出し (exportRenderer) は必ずここを通る。
//
// 対応: ラスター / グループ (pass-through、不透明度、描画モード、マスク)、
// 27 ブレンドモード、レイヤーマスク、クリッピングマスク。
// タイルピラミッド・ダーティ矩形は Phase 2。

import type { Document, Layer, LayerId, BlendMode, BlendIf } from '@/types/document'
import type { Rect } from '@/types/geometry'
import { pixelStore } from '@/pixels/pixelStore'
import type { PixelSurface } from '@/pixels/pixelSurface'
import { referencedPixelIds } from '@/model/layerTree'
import { SELECTIVE_COLOR_RANGES } from '@/types/document'
import { GLProgram } from '@/gl/program'
import { Quad } from '@/gl/quad'
import { FboPool } from '@/gl/fboPool'
import { type Framebuffer, type Texture } from '@/gl/texture'
import { RECT_VERT, docToClipMatrix } from './shaders/common'
import { BLEND_FRAG, blendModeIndex } from './shaders/blend'
import { MIX_FRAG } from './shaders/view'
import { PAINT_FRAG } from './shaders/brush'
import { ADJUST_FRAG, FILL_FRAG, ADJUST_KIND } from './shaders/adjust'
import { BLUR_FRAG, SHAPE_FRAG, COLORIZE_FRAG, BEVEL_FRAG } from './shaders/styles'
import { EFFECT_ORDER, hasActiveStyles } from './styles/effectOrder'
import type { LayerStyle } from '@/types/document'
import { buildLut } from './adjust/lut'
import { hexToRgb } from '@/color/convert'
import { textRaster, shapeRaster, smartRaster } from '@/pixels/rasterCache'
import type { Adjustment, FillPaint } from '@/types/document'
import { LayerTextureCache } from './layerTexture'
import { mat3Invert } from '@/pixels/transform'

/** 描画中のストローク (Compositor が対象レイヤーに PAINT パスを掛ける) */
export interface LiveStroke {
  layerId: string
  coverage: Texture
  color: [number, number, number]
  opacity: number
  /** 0 paint, 1 erase, 2 clone, 3 dodge, 4 burn, 5 sponge+, 6 sponge-, 7 blur, 8 sharpen */
  mode: number
  cloneOffset: { x: number; y: number }
  strength: number
  lockTransparency: boolean
  selection: { texture: Texture; rect: Rect } | null
}

/** フィルターのプレビュー: 対象レイヤーのテクスチャを差し替える (文書座標 rect) */
export interface LiveFilter {
  layerId: string
  texture: Texture
  rect: Rect
}

/** 自由変形のプレビュー: 対象レイヤーを行列 (文書 → 文書) で描く */
export interface LiveTransform {
  layerId: string
  matrix: import('@/types/geometry').Mat3
}

export interface CompositeResult {
  texture: Texture
  width: number
  height: number
}

export interface RenderStats {
  /** 直近の render が部分再合成だったか */
  partial: boolean
  /** 合成パスの数 */
  passes: number
}

interface SrcTex {
  texture: Texture
  /** 文書座標での矩形 */
  rect: Rect
  /** 文書座標 → テクセル座標の行列 (指定時は rect の代わりに使う) */
  inv?: Float32Array
}

/** ping-pong 用のペア。dst に現在の結果が入っている */
interface Target {
  dst: Framebuffer
  tmp: Framebuffer
}

export class Compositor {
  private blend: GLProgram
  private mix: GLProgram
  private paint: GLProgram
  private adjust: GLProgram
  private fill: GLProgram
  private blur: GLProgram
  private shape: GLProgram
  private colorize: GLProgram
  private bevel: GLProgram
  private quad: Quad
  /** 調整 / 塗りつぶしの LUT テクスチャ (パラメータ JSON をキーにキャッシュ) */
  private lutCache = new Map<string, { key: string; tex: WebGLTexture; lastUsed: number }>()
  private lutTick = 0
  liveStroke: LiveStroke | null = null
  liveTransform: LiveTransform | null = null
  liveFilter: LiveFilter | null = null
  private pool: FboPool
  readonly layerTextures: LayerTextureCache
  private size = { w: 0, h: 0 }
  private current: Framebuffer | null = null
  private used = new Set<string>()
  /** 描画中の文書 (パターン参照用) */
  private currentDoc: Document | null = null
  private toClip: Float32Array = new Float32Array(9)
  /** 現在の render で有効なダーティ矩形 (null = 全体) */
  private dirty: Rect | null = null
  private passes = 0
  lastStats: RenderStats = { partial: false, passes: 0 }

  constructor(private gl: WebGL2RenderingContext, maxTextureSize: number) {
    this.blend = new GLProgram(gl, RECT_VERT, BLEND_FRAG, 'blend')
    this.mix = new GLProgram(gl, RECT_VERT, MIX_FRAG, 'mix')
    this.paint = new GLProgram(gl, RECT_VERT, PAINT_FRAG, 'paint')
    this.adjust = new GLProgram(gl, RECT_VERT, ADJUST_FRAG, 'adjust')
    this.fill = new GLProgram(gl, RECT_VERT, FILL_FRAG, 'fill')
    this.blur = new GLProgram(gl, RECT_VERT, BLUR_FRAG, 'blur')
    this.shape = new GLProgram(gl, RECT_VERT, SHAPE_FRAG, 'shape')
    this.colorize = new GLProgram(gl, RECT_VERT, COLORIZE_FRAG, 'colorize')
    this.bevel = new GLProgram(gl, RECT_VERT, BEVEL_FRAG, 'bevel')
    this.quad = new Quad(gl)
    this.pool = new FboPool(gl)
    this.layerTextures = new LayerTextureCache(gl, maxTextureSize)
  }

  /**
   * 文書を合成し、結果テクスチャを返す (次の render まで有効)。
   * @param dirty 前回の結果から変わった範囲 (文書座標)。null なら全体を再合成する。
   *   部分再合成は前回の結果が同じサイズで残っているときだけ有効。
   */
  render(doc: Document, dirty: Rect | null = null): CompositeResult {
    const gl = this.gl
    const { width: w, height: h } = doc.meta
    const sameSize = this.size.w === w && this.size.h === h
    // 文書サイズが変わるとプールは FBO を全部削除する。直前の結果への参照もここで手放す。
    // 手放さないと、削除済みの FBO を release() でプールに戻し、次の acquire() がそれを
    // 返してしまい、描き込みが無かったことになる (カンバスサイズ変更後に合成が空になる)。
    if (!sameSize) this.current = null
    this.pool.setSize(w, h)
    let region: Rect | null = null
    if (dirty && sameSize && this.current) {
      const clipped = intersect(dirty, { x: 0, y: 0, w, h })
      if (!clipped) {
        // 文書外だけ → 何もしない
        this.lastStats = { partial: true, passes: 0 }
        return { texture: this.current.texture, width: w, height: h }
      }
      region = clipped
    }
    this.size = { w, h }
    this.toClip = docToClipMatrix(w, h)
    this.used = new Set()
    this.currentDoc = doc
    this.passes = 0
    this.layerTextures.resetStats()

    gl.disable(gl.BLEND)
    gl.disable(gl.DEPTH_TEST)
    gl.viewport(0, 0, w, h)

    let target: Target
    if (region && this.current) {
      // 部分再合成: もう一方の FBO を現在の結果で揃えてから、矩形内だけ描き直す
      const other = this.pool.acquire()
      this.copy(this.current, other)
      target = { dst: this.current, tmp: other }
      this.current = null
      this.dirty = region
      gl.enable(gl.SCISSOR_TEST)
      gl.scissor(region.x, region.y, region.w, region.h)
    } else {
      if (this.current) {
        this.pool.release(this.current)
        this.current = null
      }
      this.dirty = null
      gl.disable(gl.SCISSOR_TEST)
      target = { dst: this.pool.acquire(), tmp: this.pool.acquire() }
    }
    const bg = doc.meta.background
    const [r, g, b, a] = bg === 'transparent' ? [0, 0, 0, 0] : bg === 'white' ? [1, 1, 1, 1] : bg === 'black' ? [0, 0, 0, 1] : hexToRgba(bg.hex)
    this.clear(target.dst, r, g, b, a)

    this.compositeList(doc, doc.rootChildren, target)

    gl.disable(gl.SCISSOR_TEST)
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    this.pool.release(target.tmp)
    this.current = target.dst
    // 縮小表示用のミップマップ (合成結果のみ)
    gl.bindTexture(gl.TEXTURE_2D, target.dst.texture.tex)
    gl.generateMipmap(gl.TEXTURE_2D)
    gl.bindTexture(gl.TEXTURE_2D, null)
    this.layerTextures.retainOnly(this.used)
    this.layerTextures.trimToBudget(this.used)
    this.lastStats = { partial: region !== null, passes: this.passes }
    return { texture: target.dst.texture, width: w, height: h }
  }

  /** 直前の結果 (再合成なし)。まだ無ければ null */
  get cached(): CompositeResult | null {
    return this.current ? { texture: this.current.texture, width: this.size.w, height: this.size.h } : null
  }

  private clear(f: Framebuffer, r: number, g: number, b: number, a: number) {
    const gl = this.gl
    gl.bindFramebuffer(gl.FRAMEBUFFER, f.fbo)
    gl.clearColor(r, g, b, a)
    gl.clear(gl.COLOR_BUFFER_BIT)
  }

  /** children (下→上) を target に合成する。クリッピング群をまとめて扱う */
  private compositeList(doc: Document, children: LayerId[], target: Target) {
    const layers = children.map(id => doc.layers[id]).filter((l): l is Layer => !!l)
    let i = 0
    while (i < layers.length) {
      const base = layers[i]!
      // 直上に続く clipToBelow のレイヤーを集める (ベースが非表示でも消費する)
      const clipped: Layer[] = []
      let j = i + 1
      while (j < layers.length && layers[j]!.clipToBelow) {
        clipped.push(layers[j]!)
        j++
      }
      i = j
      if (!base.visible) continue
      if (clipped.length === 0) {
        this.drawLayer(doc, base, target, null)
        continue
      }
      // クリッピング群: ベースを normal で clip FBO に描き、クリップ側はベース α で制限して合成、
      // 最後に clip FBO をベースの描画モード/不透明度/マスクで target へ
      const baseSrc = this.layerSource(doc, base)
      if (!baseSrc) continue
      const clipTarget: Target = { dst: this.pool.acquire(), tmp: this.pool.acquire() }
      this.clear(clipTarget.dst, 0, 0, 0, 0)
      this.clear(clipTarget.tmp, 0, 0, 0, 0)
      this.blendPass(clipTarget, baseSrc.src, 'normal', 1, null, null)
      for (const c of clipped) {
        if (!c.visible) continue
        this.drawLayer(doc, c, clipTarget, baseSrc.src)
      }
      baseSrc.release?.()
      const mask = this.maskSource(doc, base)
      this.blendPass(target, { texture: clipTarget.dst.texture, rect: this.fullRect() }, groupMode(base.blendMode), base.opacity, mask, null)
      this.pool.release(clipTarget.dst)
      this.pool.release(clipTarget.tmp)
    }
  }

  private fullRect(): Rect {
    return { x: 0, y: 0, w: this.size.w, h: this.size.h }
  }

  /** レイヤー 1 枚を target に合成する (clip があれば α を制限) */
  private drawLayer(doc: Document, layer: Layer, target: Target, clip: SrcTex | null) {
    if (layer.kind === 'adjustment') {
      // 下地に調整を掛けた結果を、レイヤーの描画モード/不透明度/マスク/クリップで下地に合成する
      const adjusted = this.pool.acquire()
      this.runAdjust(target.dst, adjusted, layer.id, layer.adjustment)
      const mask = this.maskSource(doc, layer)
      this.blendPass(target, { texture: adjusted.texture, rect: this.fullRect() }, layer.blendMode, layer.opacity, mask, clip, layer.blendIf ?? null)
      this.pool.release(adjusted)
      return
    }
    if (layer.kind === 'group' && layer.blendMode === 'pass-through' && !clip) {
      const mask = this.maskSource(doc, layer)
      if (layer.opacity >= 1 && !mask) {
        this.compositeList(doc, layer.children, target)
        return
      }
      // pass-through + 不透明度/マスク: 現在の下地のコピーの上に子を合成し、結果を係数で混ぜる
      // (子の描画モードは下地を見る = Photoshop の通過グループの挙動)
      const g: Target = { dst: this.pool.acquire(), tmp: this.pool.acquire() }
      this.copy(target.dst, g.dst)
      this.compositeList(doc, layer.children, g)
      this.mixPass(target, g.dst, layer.opacity, mask)
      this.pool.release(g.dst)
      this.pool.release(g.tmp)
      return
    }
    const src = this.layerSource(doc, layer)
    if (!src) return
    const mask = this.maskSource(doc, layer)
    if (hasActiveStyles(layer.styles) && layer.kind !== 'group') {
      const styled = this.styledSource(src.src, layer.styles!, layer.fillOpacity)
      src.release?.()
      this.blendPass(target, { texture: styled.texture, rect: this.fullRect() }, groupMode(layer.blendMode), layer.opacity, mask, clip, layer.blendIf ?? null)
      this.pool.release(styled)
      return
    }
    const opacity = layer.kind === 'raster' || layer.kind === 'text' || layer.kind === 'fill' || layer.kind === 'shape' || layer.kind === 'smart' ? layer.opacity * layer.fillOpacity : layer.opacity
    this.blendPass(target, src.src, groupMode(layer.blendMode), opacity, mask, clip, layer.blendIf ?? null)
    src.release?.()
  }

  // ---------- レイヤースタイル ----------

  /** 文書サイズの FBO に src (normal, opacity) を描く */
  private drawToDoc(src: SrcTex, opacity = 1): Framebuffer {
    const t: Target = { dst: this.pool.acquire(), tmp: this.pool.acquire() }
    this.clear(t.dst, 0, 0, 0, 0)
    this.clear(t.tmp, 0, 0, 0, 0)
    this.blendPass(t, src, 'normal', opacity, null, null)
    this.pool.release(t.tmp)
    return t.dst
  }

  private fullPass(p: GLProgram, to: Framebuffer, bind: () => void) {
    const gl = this.gl
    const { w, h } = this.size
    gl.bindFramebuffer(gl.FRAMEBUFFER, to.fbo)
    gl.viewport(0, 0, w, h)
    p.use()
    gl.uniform4f(p.loc('uRect'), 0, 0, w, h)
    gl.uniformMatrix3fv(p.loc('uToClip'), false, this.toClip)
    gl.uniform2f(p.loc('uDocSize'), w, h)
    bind()
    this.quad.draw()
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    this.passes++
  }

  private bindTex(unit: number, tex: WebGLTexture | null, loc: WebGLUniformLocation | null) {
    const gl = this.gl
    gl.activeTexture(gl.TEXTURE0 + unit)
    gl.bindTexture(gl.TEXTURE_2D, tex)
    gl.uniform1i(loc, unit)
  }

  /** α (R) を形状処理: オフセット、反転、しきい値 */
  private shapePass(from: Framebuffer, fromR: boolean, offset: { x: number; y: number }, invert: boolean, lo: number, hi: number): Framebuffer {
    const out = this.pool.acquire()
    const gl = this.gl
    this.fullPass(this.shape, out, () => {
      this.bindTex(0, from.texture.tex, this.shape.loc('uSrc'))
      gl.uniform2f(this.shape.loc('uOffset'), offset.x, offset.y)
      gl.uniform1i(this.shape.loc('uFromR'), fromR ? 1 : 0)
      gl.uniform1i(this.shape.loc('uInvert'), invert ? 1 : 0)
      gl.uniform1f(this.shape.loc('uLo'), lo)
      gl.uniform1f(this.shape.loc('uHi'), hi)
    })
    return out
  }

  /** R チャンネルの分離ガウスぼかし (2 パス)。from は release される */
  private blurR(from: Framebuffer, radius: number): Framebuffer {
    if (radius <= 0.01) return from
    const gl = this.gl
    const mid = this.pool.acquire()
    this.fullPass(this.blur, mid, () => {
      this.bindTex(0, from.texture.tex, this.blur.loc('uSrc'))
      gl.uniform2f(this.blur.loc('uDir'), 1, 0)
      gl.uniform1f(this.blur.loc('uRadius'), radius)
    })
    const out = this.pool.acquire()
    this.fullPass(this.blur, out, () => {
      this.bindTex(0, mid.texture.tex, this.blur.loc('uSrc'))
      gl.uniform2f(this.blur.loc('uDir'), 0, 1)
      gl.uniform1f(this.blur.loc('uRadius'), radius)
    })
    this.pool.release(mid)
    this.pool.release(from)
    return out
  }

  /** α (R) に色を付けてストレート RGBA に。maskMode: 0 なし / 1 ベース内側 / 2 ベース外側 */
  /** パターンのテクスチャ (無ければ null)。REPEAT で使う */
  private patternTextures = new Map<string, { tex: WebGLTexture; w: number; h: number; revision: number }>()
  private patternTexture(patternId: string): { tex: WebGLTexture; w: number; h: number } | null {
    const doc = this.currentDoc
    const pat = doc?.patterns.find(p => p.id === patternId)
    const surface = pat ? pixelStore.get(pat.pixelId) : undefined
    return surface ? this.surfaceTexture(surface) : null
  }
  /** 内容矩形ぴったりの補助テクスチャ (パターン / 3D LUT)。レイヤーテクスチャのキャッシュはタイル境界に揃うので使えない */
  private surfaceTexture(surface: PixelSurface): { tex: WebGLTexture; w: number; h: number } | null {
    const doc = this.currentDoc
    const b = surface.contentBounds()
    if (!b) return null
    const cur = this.patternTextures.get(surface.id)
    if (cur && cur.revision === surface.revision) return cur
    const gl = this.gl
    const tex = cur?.tex ?? gl.createTexture()!
    gl.activeTexture(gl.TEXTURE7)
    gl.bindTexture(gl.TEXTURE_2D, tex)
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, b.w, b.h, 0, gl.RGBA, gl.UNSIGNED_BYTE, surface.readRect(b))
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT)
    gl.bindTexture(gl.TEXTURE_2D, null)
    const entry = { tex, w: b.w, h: b.h, revision: surface.revision }
    this.patternTextures.set(surface.id, entry)
    // 文書から参照されない補助テクスチャは捨てる
    if (doc) {
      const alive = referencedPixelIds(doc)
      for (const [id, e] of this.patternTextures) if (!alive.has(id)) { gl.deleteTexture(e.tex); this.patternTextures.delete(id) }
    }
    return entry
  }

  private colorizePass(alpha: Framebuffer, base: Framebuffer, hex: string, opacity: number, maskMode: 0 | 1 | 2, grad?: { tex: WebGLTexture; a: { x: number; y: number }; b: { x: number; y: number }; radial: boolean }, pattern?: { tex: WebGLTexture; w: number; h: number; scale: number }): Framebuffer {
    const out = this.pool.acquire()
    const gl = this.gl
    const c = hexToRgb(hex) ?? { r: 0, g: 0, b: 0 }
    this.fullPass(this.colorize, out, () => {
      this.bindTex(0, alpha.texture.tex, this.colorize.loc('uAlpha'))
      this.bindTex(1, base.texture.tex, this.colorize.loc('uBase'))
      this.bindTex(2, grad ? grad.tex : null, this.colorize.loc('uGrad'))
      this.bindTex(3, pattern ? pattern.tex : null, this.colorize.loc('uPattern'))
      if (pattern) {
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT)
      }
      gl.uniform2f(this.colorize.loc('uPatSize'), (pattern?.w ?? 1) * (pattern?.scale ?? 1), (pattern?.h ?? 1) * (pattern?.scale ?? 1))
      gl.uniform3f(this.colorize.loc('uColor'), c.r / 255, c.g / 255, c.b / 255)
      gl.uniform1f(this.colorize.loc('uOpacity'), opacity)
      gl.uniform1i(this.colorize.loc('uMaskMode'), maskMode)
      gl.uniform1i(this.colorize.loc('uMode'), pattern ? 3 : grad ? 2 : 0)
      gl.uniform2f(this.colorize.loc('uA'), grad?.a.x ?? 0, grad?.a.y ?? 0)
      gl.uniform2f(this.colorize.loc('uB'), grad?.b.x ?? 1, grad?.b.y ?? 0)
      gl.uniform1i(this.colorize.loc('uRadial'), grad?.radial ? 1 : 0)
    })
    return out
  }

  /** 拡張 (r > 0) / 収縮 (r < 0) の近似: ぼかし + しきい値 */
  private dilate(base: Framebuffer, r: number): Framebuffer {
    const a = this.shapePass(base, false, { x: 0, y: 0 }, r < 0, 0, 0)
    const b = this.blurR(a, Math.abs(r) * 1.2)
    // ぼかした α の低いしきい値 = 拡張、r<0 のときは反転してから同様に処理して戻す
    const out = this.shapePass(b, true, { x: 0, y: 0 }, r < 0, 0.08, 0.2)
    this.pool.release(b)
    return out
  }

  /**
   * レイヤースタイルを適用した文書サイズ FBO を返す (呼び出し側が release)。
   * src はレイヤー本体、fillOpacity は本体だけに掛かる。
   */
  private styledSource(src: SrcTex, styles: LayerStyle, fillOpacity: number): Framebuffer {
    const gl = this.gl
    const base = this.drawToDoc(src, 1)
    const acc: Target = { dst: this.pool.acquire(), tmp: this.pool.acquire() }
    this.clear(acc.dst, 0, 0, 0, 0)
    this.clear(acc.tmp, 0, 0, 0, 0)
    const angleVec = (deg: number, dist: number) => ({ x: -Math.cos((deg * Math.PI) / 180) * dist, y: Math.sin((deg * Math.PI) / 180) * dist })
    const fullRect = this.fullRect()
    const blendFx = (fb: Framebuffer, mode: BlendMode, clipToBase = false) => {
      this.blendPass(acc, { texture: fb.texture, rect: fullRect }, mode, 1, null, clipToBase ? { texture: base.texture, rect: fullRect } : null)
      this.pool.release(fb)
    }
    for (const fx of EFFECT_ORDER) {
      switch (fx) {
        case 'dropShadow': {
          const e = styles.dropShadow
          if (!e?.enabled) break
          const off = angleVec(e.angle, e.distance)
          let a = this.shapePass(base, false, off, false, 0, 0)
          a = this.blurR(a, e.size)
          if (e.spread > 0) {
            const sp = this.shapePass(a, true, { x: 0, y: 0 }, false, 0, Math.max(0.01, 1 - e.spread / 100))
            this.pool.release(a)
            a = sp
          }
          const col = this.colorizePass(a, base, e.hex, e.opacity, 0)
          this.pool.release(a)
          blendFx(col, e.blendMode)
          break
        }
        case 'outerGlow': {
          const e = styles.outerGlow
          if (!e?.enabled) break
          let a = this.shapePass(base, false, { x: 0, y: 0 }, false, 0, 0)
          a = this.blurR(a, e.size)
          {
            // Photoshop の「範囲 50%」: エッジ (ぼかし後 0.5) で最大になるよう再マップ
            const rm = this.shapePass(a, true, { x: 0, y: 0 }, false, 0, Math.max(0.05, 0.5 * (1 - e.spread / 100)))
            this.pool.release(a)
            a = rm
          }
          const col = this.colorizePass(a, base, e.hex, e.opacity, 2)
          this.pool.release(a)
          blendFx(col, e.blendMode)
          break
        }
        case 'strokeOutside': {
          const e = styles.stroke
          if (!e?.enabled || e.position === 'inside' || e.size <= 0) break
          const r = e.position === 'center' ? e.size / 2 : e.size
          const d = this.dilate(base, r)
          const col = this.colorizePass(d, base, e.hex, e.opacity, 2)
          this.pool.release(d)
          blendFx(col, e.blendMode)
          break
        }
        case 'body': {
          this.blendPass(acc, { texture: base.texture, rect: fullRect }, 'normal', fillOpacity, null, null)
          break
        }
        case 'innerShadow': {
          const e = styles.innerShadow
          if (!e?.enabled) break
          const off = angleVec(e.angle, e.distance)
          let a = this.shapePass(base, false, off, true, 0, 0)
          a = this.blurR(a, e.size)
          if (e.spread > 0) {
            const sp = this.shapePass(a, true, { x: 0, y: 0 }, false, 0, Math.max(0.01, 1 - e.spread / 100))
            this.pool.release(a)
            a = sp
          }
          const col = this.colorizePass(a, base, e.hex, e.opacity, 1)
          this.pool.release(a)
          blendFx(col, e.blendMode, true)
          break
        }
        case 'innerGlow': {
          const e = styles.innerGlow
          if (!e?.enabled) break
          let a = this.shapePass(base, false, { x: 0, y: 0 }, e.source !== 'center', 0, 0)
          a = this.blurR(a, e.size)
          {
            const rm = this.shapePass(a, true, { x: 0, y: 0 }, false, 0, Math.max(0.05, 0.5 * (1 - e.spread / 100)))
            this.pool.release(a)
            a = rm
          }
          const col = this.colorizePass(a, base, e.hex, e.opacity, 1)
          this.pool.release(a)
          blendFx(col, e.blendMode, true)
          break
        }
        case 'bevel': {
          const e = styles.bevel
          if (!e?.enabled) break
          let hgt = this.shapePass(base, false, { x: 0, y: 0 }, false, 0, 0)
          hgt = this.blurR(hgt, Math.max(0.5, e.size))
          if (e.soften > 0) hgt = this.blurR(hgt, e.soften)
          const az = (e.angle * Math.PI) / 180, alt = (e.altitude * Math.PI) / 180
          const light = [-Math.cos(az) * Math.cos(alt), Math.sin(az) * Math.cos(alt), Math.sin(alt)]
          const hi = hexToRgb(e.highlightHex) ?? { r: 255, g: 255, b: 255 }
          const sh = hexToRgb(e.shadowHex) ?? { r: 0, g: 0, b: 0 }
          const styleIdx = e.style === 'inner' ? 0 : e.style === 'outer' ? 1 : 2
          for (const which of [1, 0]) {
            const out = this.pool.acquire()
            this.fullPass(this.bevel, out, () => {
              this.bindTex(0, hgt.texture.tex, this.bevel.loc('uHeight'))
              this.bindTex(1, base.texture.tex, this.bevel.loc('uBase'))
              gl.uniform3f(this.bevel.loc('uLight'), light[0]!, light[1]!, light[2]!)
              gl.uniform1f(this.bevel.loc('uDepth'), (e.depth / 100) * Math.max(1, e.size) * 2)
              gl.uniform1i(this.bevel.loc('uStyle'), styleIdx)
              gl.uniform3f(this.bevel.loc('uHiColor'), hi.r / 255, hi.g / 255, hi.b / 255)
              gl.uniform1f(this.bevel.loc('uHiOpacity'), e.highlightOpacity)
              gl.uniform3f(this.bevel.loc('uShColor'), sh.r / 255, sh.g / 255, sh.b / 255)
              gl.uniform1f(this.bevel.loc('uShOpacity'), e.shadowOpacity)
              gl.uniform1i(this.bevel.loc('uWhich'), which)
            })
            blendFx(out, which === 0 ? 'screen' : 'multiply', e.style === 'inner')
          }
          this.pool.release(hgt)
          break
        }
        case 'satin': {
          const e = styles.satin
          if (!e?.enabled) break
          const off = angleVec(e.angle, e.distance)
          const a1 = this.blurR(this.shapePass(base, false, off, false, 0, 0), e.size)
          const a2 = this.blurR(this.shapePass(base, false, { x: -off.x, y: -off.y }, false, 0, 0), e.size)
          // 2 つの差 (|a1 - a2|) を α にする: colorize は R を読むので shape で合成する代わりに 2 回描く
          const col1 = this.colorizePass(a1, base, e.hex, e.opacity, 1)
          const col2 = this.colorizePass(a2, base, e.hex, e.opacity, 1)
          this.pool.release(a1)
          this.pool.release(a2)
          // difference で重ねると帯状の模様になる (近似)
          const satinT: Target = { dst: col1, tmp: this.pool.acquire() }
          this.clear(satinT.tmp, 0, 0, 0, 0)
          this.blendPass(satinT, { texture: col2.texture, rect: fullRect }, e.invert ? 'difference' : 'normal', 1, null, null)
          this.pool.release(col2)
          this.pool.release(satinT.tmp)
          blendFx(satinT.dst, e.blendMode, true)
          break
        }
        case 'colorOverlay': {
          const e = styles.colorOverlay
          if (!e?.enabled) break
          const a = this.shapePass(base, false, { x: 0, y: 0 }, false, 0, 0)
          const col = this.colorizePass(a, base, e.hex, e.opacity, 0)
          this.pool.release(a)
          blendFx(col, e.blendMode, true)
          break
        }
        case 'patternOverlay': {
          const e = styles.patternOverlay
          if (!e?.enabled) break
          const pat = this.patternTexture(e.patternId)
          if (!pat) break
          const a = this.shapePass(base, false, { x: 0, y: 0 }, false, 0, 0)
          const col = this.colorizePass(a, base, '#000000', e.opacity, 0, undefined, { ...pat, scale: e.scale })
          this.pool.release(a)
          blendFx(col, e.blendMode, true)
          break
        }
        case 'gradientOverlay': {
          const e = styles.gradientOverlay
          if (!e?.enabled) break
          const stops = e.stops ?? [{ pos: 0, hex: '#000000' }, { pos: 1, hex: '#ffffff' }]
          const tex = this.lutTexture('fx:' + JSON.stringify(stops), JSON.stringify(stops), () => {
            const out = new Uint8ClampedArray(256 * 4)
            const st = [...stops].map(s => ({ pos: s.pos, c: hexToRgb(s.hex) ?? { r: 0, g: 0, b: 0 } })).sort((a, b) => a.pos - b.pos)
            for (let i = 0; i < 256; i++) {
              const t = i / 255
              let k = 0
              while (k + 1 < st.length && t > st[k + 1]!.pos) k++
              const a = st[k]!, b = st[Math.min(k + 1, st.length - 1)]!
              const f = t <= a.pos ? 0 : t >= b.pos ? 1 : (t - a.pos) / (b.pos - a.pos)
              out[i * 4] = a.c.r + (b.c.r - a.c.r) * f; out[i * 4 + 1] = a.c.g + (b.c.g - a.c.g) * f; out[i * 4 + 2] = a.c.b + (b.c.b - a.c.b) * f; out[i * 4 + 3] = 255
            }
            return out
          })
          const r = src.rect
          const cx = r.x + r.w / 2, cy = r.y + r.h / 2
          const ang = ((e.angle ?? 90) * Math.PI) / 180
          const len = Math.max(r.w, r.h) / 2
          const dx = Math.cos(ang) * len, dy = -Math.sin(ang) * len
          const a = this.shapePass(base, false, { x: 0, y: 0 }, false, 0, 0)
          const col = this.colorizePass(a, base, e.hex, e.opacity, 0, { tex, a: e.style === 'radial' ? { x: cx, y: cy } : { x: cx - dx, y: cy - dy }, b: { x: cx + dx, y: cy + dy }, radial: e.style === 'radial' })
          this.pool.release(a)
          blendFx(col, e.blendMode, true)
          break
        }
        case 'strokeInside': {
          const e = styles.stroke
          if (!e?.enabled || e.position === 'outside' || e.size <= 0) break
          const r = e.position === 'center' ? e.size / 2 : e.size
          // 内側: ベース α − 収縮 α
          const eroded = this.dilate(base, -r)
          const inv = this.shapePass(eroded, true, { x: 0, y: 0 }, true, 0, 0)
          this.pool.release(eroded)
          const col = this.colorizePass(inv, base, e.hex, e.opacity, 1)
          this.pool.release(inv)
          blendFx(col, e.blendMode, true)
          break
        }
      }
    }
    this.pool.release(base)
    this.pool.release(acc.tmp)
    return acc.dst
  }

  /** レイヤーをテクスチャとして得る。グループは子を合成した FBO (呼び出し側が release する) */
  private layerSource(doc: Document, layer: Layer): { src: SrcTex; release?: () => void } | null {
    if (layer.kind === 'raster') {
      const live = this.liveStroke && this.liveStroke.layerId === layer.id ? this.liveStroke : null
      if (live) {
        const painted = this.paintLayer(layer, live)
        if (!painted) return null
        return { src: { texture: painted.texture, rect: this.fullRect() }, release: () => this.pool.release(painted) }
      }
      const lf = this.liveFilter && this.liveFilter.layerId === layer.id ? this.liveFilter : null
      if (lf) return { src: { texture: lf.texture, rect: lf.rect } }
      const surface = pixelStore.get(layer.pixelId)
      if (!surface) return null
      this.used.add(surface.id)
      const t = this.layerTextures.get(surface)
      if (!t) return null
      const rect = { x: t.rect.x + layer.offset.x, y: t.rect.y + layer.offset.y, w: t.rect.w, h: t.rect.h }
      const lt = this.liveTransform && this.liveTransform.layerId === layer.id ? this.liveTransform : null
      if (lt) {
        // 文書座標 p → 変形前の文書座標 M^-1 p → テクセル (rect 原点を引く)
        const inv = mat3Invert(lt.matrix)
        if (inv) {
          const m = inv
          // translate(-rect.x, -rect.y) * inv
          const f = new Float32Array([m[0], m[1], m[2], m[3], m[4], m[5], m[6] - rect.x, m[7] - rect.y, m[8]])
          return { src: { texture: t.texture, rect, inv: f } }
        }
      }
      return { src: { texture: t.texture, rect } }
    }
    if (layer.kind === 'text' || layer.kind === 'shape' || layer.kind === 'smart') {
      const r = layer.kind === 'text' ? textRaster(layer) : layer.kind === 'shape' ? shapeRaster(layer) : smartRaster(layer, pixelStore.get(layer.sourceId))
      if (!r) return null
      this.used.add(r.surface.id)
      const t = this.layerTextures.get(r.surface)
      if (!t) return null
      const rect = { x: t.rect.x + r.offset.x, y: t.rect.y + r.offset.y, w: t.rect.w, h: t.rect.h }
      const lt = this.liveTransform && this.liveTransform.layerId === layer.id ? this.liveTransform : null
      if (lt) {
        const inv = mat3Invert(lt.matrix)
        if (inv) return { src: { texture: t.texture, rect, inv: new Float32Array([inv[0], inv[1], inv[2], inv[3], inv[4], inv[5], inv[6] - rect.x, inv[7] - rect.y, inv[8]]) } }
      }
      return { src: { texture: t.texture, rect } }
    }
    if (layer.kind === 'fill') {
      const f = this.pool.acquire()
      this.runFill(f, layer.id, layer.fill)
      return { src: { texture: f.texture, rect: this.fullRect() }, release: () => this.pool.release(f) }
    }
    if (layer.kind === 'adjustment') return null
    // グループ: 透明な FBO に子を合成
    const g: Target = { dst: this.pool.acquire(), tmp: this.pool.acquire() }
    this.clear(g.dst, 0, 0, 0, 0)
    this.clear(g.tmp, 0, 0, 0, 0)
    this.compositeList(doc, layer.children, g)
    this.pool.release(g.tmp)
    return {
      src: { texture: g.dst.texture, rect: this.fullRect() },
      release: () => this.pool.release(g.dst)
    }
  }

  /**
   * ラスターレイヤーを文書空間の FBO に描き、ライブストロークを PAINT パスで乗せる。
   * 戻り値の FBO は呼び出し側が release する。
   */
  private paintLayer(layer: Layer & { kind: 'raster' }, live: LiveStroke): Framebuffer | null {
    const gl = this.gl
    const { w, h } = this.size
    const surface = pixelStore.get(layer.pixelId)
    if (!surface) return null
    this.used.add(surface.id)
    const base = this.pool.acquire()
    this.clear(base, 0, 0, 0, 0)
    const t = this.layerTextures.get(surface)
    if (t) {
      const tgt: Target = { dst: base, tmp: this.pool.acquire() }
      this.clear(tgt.tmp, 0, 0, 0, 0)
      this.blendPass(tgt, { texture: t.texture, rect: { x: t.rect.x + layer.offset.x, y: t.rect.y + layer.offset.y, w: t.rect.w, h: t.rect.h } }, 'normal', 1, null, null)
      // blendPass は ping-pong で dst を入れ替えるので、結果が入っている方を採用
      this.pool.release(tgt.tmp)
      const result = tgt.dst
      const out = this.pool.acquire()
      this.runPaint(result, out, live, w, h)
      this.pool.release(result)
      return out
    }
    const out = this.pool.acquire()
    this.runPaint(base, out, live, w, h)
    this.pool.release(base)
    return out
  }

  private runPaint(from: Framebuffer, to: Framebuffer, live: LiveStroke, w: number, h: number) {
    const gl = this.gl
    gl.bindFramebuffer(gl.FRAMEBUFFER, to.fbo)
    gl.viewport(0, 0, w, h)
    const p = this.paint
    p.use()
    gl.uniform4f(p.loc('uRect'), 0, 0, w, h)
    gl.uniformMatrix3fv(p.loc('uToClip'), false, this.toClip)
    gl.uniform2f(p.loc('uDocSize'), w, h)
    gl.uniform3f(p.loc('uColor'), live.color[0], live.color[1], live.color[2])
    gl.uniform1f(p.loc('uOpacity'), live.opacity)
    gl.uniform1i(p.loc('uMode'), live.mode)
    gl.uniform2f(p.loc('uCloneOffset'), live.cloneOffset.x, live.cloneOffset.y)
    gl.uniform1f(p.loc('uStrength'), live.strength)
    gl.uniform1i(p.loc('uLockTransparency'), live.lockTransparency ? 1 : 0)
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, from.texture.tex)
    gl.uniform1i(p.loc('uDst'), 0)
    gl.activeTexture(gl.TEXTURE1)
    gl.bindTexture(gl.TEXTURE_2D, live.coverage.tex)
    gl.uniform1i(p.loc('uCov'), 1)
    gl.activeTexture(gl.TEXTURE2)
    gl.bindTexture(gl.TEXTURE_2D, live.selection ? live.selection.texture.tex : null)
    gl.uniform1i(p.loc('uSel'), 2)
    gl.uniform1i(p.loc('uHasSel'), live.selection ? 1 : 0)
    const sr = live.selection?.rect ?? { x: 0, y: 0, w: 1, h: 1 }
    gl.uniform4f(p.loc('uSelRect'), sr.x, sr.y, sr.w, sr.h)
    this.quad.draw()
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
  }

  /**
   * ライブストロークを確定する: 対象レイヤーの PAINT 結果から rect (文書座標) を読み出す。
   * 戻り値の rgba はストレートアルファ、文書座標系 (レイヤー座標へは呼び出し側が offset を引く)。
   */
  bakeLiveStroke(doc: Document, rect: Rect): Uint8ClampedArray | null {
    const live = this.liveStroke
    if (!live) return null
    const layer = doc.layers[live.layerId]
    if (!layer || layer.kind !== 'raster') return null
    const gl = this.gl
    const { width: w, height: h } = doc.meta
    if (this.size.w !== w || this.size.h !== h) this.current = null // 同上
    this.pool.setSize(w, h)
    this.size = { w, h }
    this.toClip = docToClipMatrix(w, h)
    gl.disable(gl.BLEND)
    gl.disable(gl.SCISSOR_TEST)
    this.dirty = null
    const painted = this.paintLayer(layer, live)
    if (!painted) return null
    const out = new Uint8Array(rect.w * rect.h * 4)
    gl.bindFramebuffer(gl.FRAMEBUFFER, painted.fbo)
    gl.pixelStorei(gl.PACK_ALIGNMENT, 1)
    gl.readPixels(rect.x, rect.y, rect.w, rect.h, gl.RGBA, gl.UNSIGNED_BYTE, out)
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    this.pool.release(painted)
    return new Uint8ClampedArray(out.buffer)
  }

  private lutTexture(id: string, key: string, build: () => Uint8ClampedArray): WebGLTexture {
    const gl = this.gl
    const cur = this.lutCache.get(id)
    if (cur && cur.key === key) {
      cur.lastUsed = ++this.lutTick
      return cur.tex
    }
    const tex = cur?.tex ?? gl.createTexture()!
    gl.activeTexture(gl.TEXTURE7)
    gl.bindTexture(gl.TEXTURE_2D, tex)
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, 256, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, build())
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.bindTexture(gl.TEXTURE_2D, null)
    this.lutCache.set(id, { key, tex, lastUsed: ++this.lutTick })
    return tex
  }

  private runAdjust(from: Framebuffer, to: Framebuffer, layerId: string, adj: Adjustment) {
    const gl = this.gl
    const { w, h } = this.size
    // LUT / パラメータを先に用意する (lutTexture はテクスチャのバインドを触るので、uDst のバインドより前に)
    const key = JSON.stringify(adj)
    const P = [[0, 0, 0, 0], [0, 0, 0, 0], [0, 0, 0, 0], [0, 0, 0, 0]] as number[][]
    let sel: Float32Array | null = null
    let lut3d: { tex: WebGLTexture; n: number } | null = null
    let kind: number = ADJUST_KIND.lut
    let lutTex: WebGLTexture | null = null
    const lut = buildLut(adj)
    if (lut) {
      lutTex = this.lutTexture('adj:' + layerId, key, () => lut)
      kind = adj.kind === 'gradient-map' ? ADJUST_KIND.gradientMap : adj.kind === 'threshold' ? ADJUST_KIND.threshold : ADJUST_KIND.lut
      if (adj.kind === 'threshold') P[0] = [adj.level, 0, 0, 0]
    } else {
      switch (adj.kind) {
        case 'hue-saturation':
          kind = ADJUST_KIND.hueSaturation
          P[0] = [adj.master.hue / 360, adj.master.saturation / 100, adj.master.lightness / 100, adj.colorize ? 1 : 0]
          P[1] = [adj.colorizeHue / 360, adj.colorizeSaturation / 100, adj.colorizeLightness / 100, 0]
          break
        case 'vibrance':
          kind = ADJUST_KIND.vibrance
          P[0] = [adj.vibrance / 100, adj.saturation / 100, 0, 0]
          break
        case 'color-balance':
          kind = ADJUST_KIND.colorBalance
          P[0] = [adj.shadows[0] / 100, adj.shadows[1] / 100, adj.shadows[2] / 100, 0]
          P[1] = [adj.midtones[0] / 100, adj.midtones[1] / 100, adj.midtones[2] / 100, 0]
          P[2] = [adj.highlights[0] / 100, adj.highlights[1] / 100, adj.highlights[2] / 100, 0]
          P[3] = [adj.preserveLuminosity ? 1 : 0, 0, 0, 0]
          break
        case 'black-white': {
          kind = ADJUST_KIND.blackWhite
          const t = hexToRgb(adj.tintHex) ?? { r: 230, g: 200, b: 150 }
          P[0] = [adj.reds / 100, adj.yellows / 100, adj.greens / 100, adj.cyans / 100]
          P[1] = [adj.blues / 100, adj.magentas / 100, adj.tint ? 1 : 0, 0]
          P[2] = [t.r / 255, t.g / 255, t.b / 255, 0]
          break
        }
        case 'photo-filter': {
          kind = ADJUST_KIND.photoFilter
          const c = hexToRgb(adj.hex) ?? { r: 236, g: 138, b: 0 }
          P[0] = [c.r / 255, c.g / 255, c.b / 255, 0]
          P[1] = [adj.density / 100, adj.preserveLuminosity ? 1 : 0, 0, 0]
          break
        }
        case 'channel-mixer':
          kind = ADJUST_KIND.channelMixer
          P[0] = adj.r.map(v => v / 100)
          P[1] = adj.g.map(v => v / 100)
          P[2] = adj.b.map(v => v / 100)
          P[3] = [adj.monochrome ? 1 : 0, 0, 0, 0]
          break
        case 'color-lookup': {
          kind = ADJUST_KIND.colorLookup
          const s = adj.lutPixelId ? pixelStore.get(adj.lutPixelId) : undefined
          const lt = s ? this.surfaceTexture(s) : null
          lut3d = lt ? { tex: lt.tex, n: adj.size } : null
          break
        }
        case 'selective-color': {
          kind = ADJUST_KIND.selectiveColor
          P[0] = [adj.relative ? 1 : 0, 0, 0, 0]
          sel = new Float32Array(36)
          SELECTIVE_COLOR_RANGES.forEach((name, i) => { const q = adj.ranges[name]; for (let k = 0; k < 4; k++) sel![i * 4 + k] = q[k]! / 100 })
          break
        }
      }
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, to.fbo)
    gl.viewport(0, 0, w, h)
    const p = this.adjust
    p.use()
    gl.uniform4f(p.loc('uRect'), 0, 0, w, h)
    gl.uniformMatrix3fv(p.loc('uToClip'), false, this.toClip)
    gl.uniform2f(p.loc('uDocSize'), w, h)
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, from.texture.tex)
    gl.uniform1i(p.loc('uDst'), 0)
    gl.activeTexture(gl.TEXTURE1)
    gl.bindTexture(gl.TEXTURE_2D, lutTex)
    gl.uniform1i(p.loc('uLut'), 1)
    gl.uniform1i(p.loc('uKind'), kind)
    gl.uniform4f(p.loc('uP0'), P[0]![0]!, P[0]![1]!, P[0]![2]!, P[0]![3]!)
    gl.uniform4f(p.loc('uP1'), P[1]![0]!, P[1]![1]!, P[1]![2]!, P[1]![3]!)
    gl.uniform4f(p.loc('uP2'), P[2]![0]!, P[2]![1]!, P[2]![2]!, P[2]![3]!)
    gl.uniform4f(p.loc('uP3'), P[3]![0]!, P[3]![1]!, P[3]![2]!, P[3]![3]!)
    if (sel) gl.uniform1fv(p.loc('uSel'), sel)
    gl.activeTexture(gl.TEXTURE2)
    gl.bindTexture(gl.TEXTURE_2D, lut3d ? lut3d.tex : null)
    gl.uniform1i(p.loc('uLut3d'), 2)
    gl.uniform1f(p.loc('uLutN'), lut3d ? lut3d.n : 0)
    this.quad.draw()
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    this.passes++
  }

  private runFill(to: Framebuffer, layerId: string, fill: FillPaint) {
    const gl = this.gl
    const { w, h } = this.size
    gl.bindFramebuffer(gl.FRAMEBUFFER, to.fbo)
    gl.viewport(0, 0, w, h)
    const p = this.fill
    p.use()
    gl.uniform4f(p.loc('uRect'), 0, 0, w, h)
    gl.uniformMatrix3fv(p.loc('uToClip'), false, this.toClip)
    gl.uniform2f(p.loc('uDocSize'), w, h)
    if (fill.kind === 'pattern') {
      const pat = this.patternTexture(fill.patternId)
      gl.uniform1i(p.loc('uKind'), pat ? 6 : 0)
      gl.uniform4f(p.loc('uColor'), 0, 0, 0, 0)
      gl.activeTexture(gl.TEXTURE0)
      gl.bindTexture(gl.TEXTURE_2D, null)
      gl.uniform1i(p.loc('uStops'), 0)
      gl.activeTexture(gl.TEXTURE1)
      gl.bindTexture(gl.TEXTURE_2D, pat ? pat.tex : null)
      if (pat) {
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT)
      }
      gl.uniform1i(p.loc('uPattern'), 1)
      gl.uniform2f(p.loc('uPatSize'), (pat?.w ?? 1) * fill.scale, (pat?.h ?? 1) * fill.scale)
      gl.uniform2f(p.loc('uPatOffset'), fill.offset.x, fill.offset.y)
    } else if (fill.kind === 'solid') {
      const c = hexToRgb(fill.hex) ?? { r: 0, g: 0, b: 0 }
      gl.uniform1i(p.loc('uKind'), 0)
      gl.uniform4f(p.loc('uColor'), c.r / 255, c.g / 255, c.b / 255, 1)
      gl.activeTexture(gl.TEXTURE0)
      gl.bindTexture(gl.TEXTURE_2D, null)
      gl.uniform1i(p.loc('uStops'), 0)
    } else {
      const key = JSON.stringify(fill.stops) + fill.reverse
      const tex = this.lutTexture('fill:' + layerId, key, () => {
        const out = new Uint8ClampedArray(256 * 4)
        const st = [...fill.stops].map(s => ({ pos: fill.reverse ? 1 - s.pos : s.pos, c: hexToRgb(s.hex) ?? { r: 0, g: 0, b: 0 }, a: s.alpha })).sort((a, b) => a.pos - b.pos)
        for (let i = 0; i < 256; i++) {
          const t = i / 255
          let k = 0
          while (k + 1 < st.length && t > st[k + 1]!.pos) k++
          const a = st[k]!, b = st[Math.min(k + 1, st.length - 1)]!
          const f = t <= a.pos ? 0 : t >= b.pos ? 1 : (t - a.pos) / (b.pos - a.pos)
          out[i * 4] = a.c.r + (b.c.r - a.c.r) * f
          out[i * 4 + 1] = a.c.g + (b.c.g - a.c.g) * f
          out[i * 4 + 2] = a.c.b + (b.c.b - a.c.b) * f
          out[i * 4 + 3] = (a.a + (b.a - a.a) * f) * 255
        }
        return out
      })
      const kinds = { linear: 1, radial: 2, angle: 3, reflected: 4, diamond: 5 }
      gl.uniform1i(p.loc('uKind'), kinds[fill.style])
      // 角度と倍率から始点/終点を決める (中心基準)
      const cx = w / 2, cy = h / 2
      const ang = (fill.angle * Math.PI) / 180
      const len = (Math.max(w, h) / 2) * fill.scale
      const dx = Math.cos(ang) * len, dy = -Math.sin(ang) * len
      if (fill.style === 'linear' || fill.style === 'reflected') gl.uniform2f(p.loc('uA'), cx - dx, cy - dy)
      else gl.uniform2f(p.loc('uA'), cx, cy)
      gl.uniform2f(p.loc('uB'), cx + dx, cy + dy)
      gl.activeTexture(gl.TEXTURE0)
      gl.bindTexture(gl.TEXTURE_2D, tex)
      gl.uniform1i(p.loc('uStops'), 0)
    }
    this.quad.draw()
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    this.passes++
  }

  private maskSource(doc: Document, layer: Layer): SrcTex | null {
    void doc
    if (!layer.mask || !layer.mask.enabled) return null
    const ms = pixelStore.get(layer.mask.pixelId)
    if (!ms) return null
    this.used.add(ms.id)
    const mt = this.layerTextures.get(ms)
    // 空のマスク = すべて隠す (0)
    if (!mt) return { texture: this.emptyMask(), rect: { x: 0, y: 0, w: 1, h: 1 } }
    return { texture: mt.texture, rect: { x: mt.rect.x + layer.mask.offset.x, y: mt.rect.y + layer.mask.offset.y, w: mt.rect.w, h: mt.rect.h } }
  }

  private emptyMaskTex: Texture | null = null
  private emptyMask(): Texture {
    if (!this.emptyMaskTex) {
      const gl = this.gl
      const tex = gl.createTexture()!
      gl.bindTexture(gl.TEXTURE_2D, tex)
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, 1, 1, 0, gl.RED, gl.UNSIGNED_BYTE, new Uint8Array([0]))
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
      gl.bindTexture(gl.TEXTURE_2D, null)
      this.emptyMaskTex = { tex, width: 1, height: 1, channels: 1 }
    }
    return this.emptyMaskTex
  }

  /** FBO をそのままコピーする */
  private copy(from: Framebuffer, to: Framebuffer) {
    const gl = this.gl
    const { w, h } = this.size
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, from.fbo)
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, to.fbo)
    gl.blitFramebuffer(0, 0, w, h, 0, 0, w, h, gl.COLOR_BUFFER_BIT, gl.NEAREST)
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null)
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null)
  }

  /** target.dst = mix(target.dst, src, opacity * mask) */
  private mixPass(target: Target, src: Framebuffer, opacity: number, mask: SrcTex | null) {
    const gl = this.gl
    const { w, h } = this.size
    gl.bindFramebuffer(gl.FRAMEBUFFER, target.tmp.fbo)
    gl.viewport(0, 0, w, h)
    const p = this.mix
    p.use()
    gl.uniform4f(p.loc('uRect'), 0, 0, w, h)
    gl.uniformMatrix3fv(p.loc('uToClip'), false, this.toClip)
    gl.uniform2f(p.loc('uDocSize'), w, h)
    gl.uniform1f(p.loc('uOpacity'), opacity)
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, target.dst.texture.tex)
    gl.uniform1i(p.loc('uDst'), 0)
    gl.activeTexture(gl.TEXTURE1)
    gl.bindTexture(gl.TEXTURE_2D, src.texture.tex)
    gl.uniform1i(p.loc('uSrc'), 1)
    gl.activeTexture(gl.TEXTURE2)
    gl.bindTexture(gl.TEXTURE_2D, mask ? mask.texture.tex : null)
    gl.uniform1i(p.loc('uMask'), 2)
    gl.uniform1i(p.loc('uHasMask'), mask ? 1 : 0)
    if (mask) gl.uniform4f(p.loc('uMaskRect'), mask.rect.x, mask.rect.y, mask.rect.w, mask.rect.h)
    else gl.uniform4f(p.loc('uMaskRect'), 0, 0, 1, 1)
    this.quad.draw()
    const t = target.dst
    target.dst = target.tmp
    target.tmp = t
  }

  /** target.dst の上に src を合成して target.dst を更新する (ping-pong) */
  private blendPass(target: Target, src: SrcTex, mode: BlendMode, opacity: number, mask: SrcTex | null, clip: SrcTex | null, blendIf: BlendIf | null = null) {
    const gl = this.gl
    const { w, h } = this.size
    this.passes++
    gl.bindFramebuffer(gl.FRAMEBUFFER, target.tmp.fbo)
    gl.viewport(0, 0, w, h)
    const p = this.blend
    p.use()
    gl.uniform1i(p.loc('uBlendIf'), blendIf ? 1 : 0)
    if (blendIf) {
      gl.uniform1i(p.loc('uBlendIfCh'), blendIf.channel === 'r' ? 1 : blendIf.channel === 'g' ? 2 : blendIf.channel === 'b' ? 3 : 0)
      const t = blendIf.thisLayer, u = blendIf.underlying
      gl.uniform4f(p.loc('uBlendIfThis'), t[0] / 255, t[1] / 255, t[2] / 255, t[3] / 255)
      gl.uniform4f(p.loc('uBlendIfUnder'), u[0] / 255, u[1] / 255, u[2] / 255, u[3] / 255)
    }
    gl.uniform4f(p.loc('uRect'), 0, 0, w, h)
    gl.uniformMatrix3fv(p.loc('uToClip'), false, this.toClip)
    gl.uniform2f(p.loc('uDocSize'), w, h)
    gl.uniform4f(p.loc('uSrcRect'), src.rect.x, src.rect.y, src.rect.w, src.rect.h)
    if (src.inv) {
      gl.uniform1i(p.loc('uUseMatrix'), 1)
      gl.uniformMatrix3fv(p.loc('uSrcInv'), false, src.inv)
      gl.uniform2f(p.loc('uSrcSize'), src.rect.w, src.rect.h)
    } else {
      gl.uniform1i(p.loc('uUseMatrix'), 0)
    }
    gl.uniform1f(p.loc('uOpacity'), opacity)
    gl.uniform1i(p.loc('uMode'), blendModeIndex(mode))
    gl.uniform1f(p.loc('uSeed'), 0.37)
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, target.dst.texture.tex)
    gl.uniform1i(p.loc('uDst'), 0)
    gl.activeTexture(gl.TEXTURE1)
    gl.bindTexture(gl.TEXTURE_2D, src.texture.tex)
    gl.uniform1i(p.loc('uSrc'), 1)
    gl.activeTexture(gl.TEXTURE2)
    gl.bindTexture(gl.TEXTURE_2D, mask ? mask.texture.tex : null)
    gl.uniform1i(p.loc('uMask'), 2)
    gl.uniform1i(p.loc('uHasMask'), mask ? 1 : 0)
    if (mask) gl.uniform4f(p.loc('uMaskRect'), mask.rect.x, mask.rect.y, mask.rect.w, mask.rect.h)
    else gl.uniform4f(p.loc('uMaskRect'), 0, 0, 1, 1)
    gl.activeTexture(gl.TEXTURE3)
    gl.bindTexture(gl.TEXTURE_2D, clip ? clip.texture.tex : null)
    gl.uniform1i(p.loc('uClip'), 3)
    gl.uniform1i(p.loc('uHasClip'), clip ? 1 : 0)
    if (clip) gl.uniform4f(p.loc('uClipRect'), clip.rect.x, clip.rect.y, clip.rect.w, clip.rect.h)
    else gl.uniform4f(p.loc('uClipRect'), 0, 0, 1, 1)
    this.quad.draw()
    const t = target.dst
    target.dst = target.tmp
    target.tmp = t
  }

  /** 最後の合成結果から矩形を読み出す (ストレートアルファ RGBA、行順 = 文書行順) */
  readPixels(rect: Rect): Uint8ClampedArray {
    const gl = this.gl
    if (!this.current) throw new Error('まだ合成していない')
    const out = new Uint8Array(rect.w * rect.h * 4)
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.current.fbo)
    gl.pixelStorei(gl.PACK_ALIGNMENT, 1)
    gl.readPixels(rect.x, rect.y, rect.w, rect.h, gl.RGBA, gl.UNSIGNED_BYTE, out)
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    return new Uint8ClampedArray(out.buffer)
  }

  get fboCount(): number {
    return this.pool.count
  }

  /** コンテキスト喪失時などに全 GPU リソースを捨てる (CPU タイルから再構築できる) */
  dispose(): void {
    for (const e of this.patternTextures.values()) this.gl.deleteTexture(e.tex)
    this.patternTextures.clear()
    this.layerTextures.clear()
    this.pool.disposeAll()
    this.current = null
    if (this.emptyMaskTex) {
      this.gl.deleteTexture(this.emptyMaskTex.tex)
      this.emptyMaskTex = null
    }
    this.blend.dispose()
    this.mix.dispose()
    this.paint.dispose()
    this.adjust.dispose()
    this.fill.dispose()
    this.blur.dispose()
    this.shape.dispose()
    this.colorize.dispose()
    this.bevel.dispose()
    for (const e of this.lutCache.values()) this.gl.deleteTexture(e.tex)
    this.lutCache.clear()
    this.quad.dispose()
  }
}

function intersect(a: Rect, b: Rect): Rect | null {
  const x0 = Math.max(a.x, b.x), y0 = Math.max(a.y, b.y)
  const x1 = Math.min(a.x + a.w, b.x + b.w), y1 = Math.min(a.y + a.h, b.y + b.h)
  if (x1 <= x0 || y1 <= y0) return null
  return { x: Math.floor(x0), y: Math.floor(y0), w: Math.ceil(x1) - Math.floor(x0), h: Math.ceil(y1) - Math.floor(y0) }
}

function groupMode(mode: BlendMode | 'pass-through'): BlendMode {
  return mode === 'pass-through' ? 'normal' : mode
}

function hexToRgba(hex: string): [number, number, number, number] {
  const n = parseInt(hex.slice(1), 16)
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255, 1]
}
